import { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FJDropdownNew from '../../components/FJDropDown/FJDropdownNew';
import FJLocalStore from '../../../store/FJLocalStore';
import FJUtil from '../../../util/FJUtil';
import ArrowIcon from '../../../assets/svg/button-right.svg?react';

import FJModelFilterTabs from './components/FJModelFilterTabs';
import FJModelList from './components/FJModelList';
import FJModelItem from './components/FJModelItem';

import styles from './FJModelSelectorPanel.module.less';

const FJModelSelectorPanel = ({
    modelList = [],
    filterConfig = [],
    currentModelValue,
    onSelect,
    currentMode = null,
    title = 'models',
    boxStyle = {},
    className = '',
    offsetLeft = 0,
    offsetTop = 0,
    maxHeight = 'auto',
    sideModelClassName = '',
}) => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [isShow, setIsShow] = useState(false);

    useEffect(() => {
        setActiveFilter('all');
    }, [filterConfig]);

    const curModelObj = useMemo(() => {
        return modelList.find(item => item.value === currentModelValue);
    }, [modelList, currentModelValue]);

    const filteredList = useMemo(() => {
        if (activeFilter === 'all') return modelList;
        return modelList.filter(model => {
            const categories = currentMode ? model.categoryByMode?.[currentMode] || [] : model.categorys || [];
            return categories.includes(activeFilter);
        });
    }, [modelList, activeFilter, currentMode]);

    const handleSelect = useCallback(
        model => {
            onSelect(model);
        },
        [onSelect],
    );

    const renderTrigger = ({ ref, active, onClick }) => {
        return (
            <div className={classNames(styles.model_box, className)} style={boxStyle} ref={ref}>
                <div className={styles.model_title}>{FJLocalStore._(title)}</div>
                <div className={styles.model_btu} onClick={onClick}>
                    {curModelObj && (
                        <div className={styles.model_icon_box}>
                            <img src={curModelObj?.renderIcon} className={styles.model_icon} />
                        </div>
                    )}
                    {FJLocalStore._(curModelObj?.name)}
                    <ArrowIcon className={classNames(styles.arrow_icon, { [styles.arrow_icon_active]: active })} />
                </div>
            </div>
        );
    };

    const renderOverlay = ({ ref }) => {
        return (
            <div
                className={classNames(styles.panel_overlay, sideModelClassName)}
                ref={ref}
                style={{ maxHeight: maxHeight }}
            >
                <div className={styles.panel_header}>
                    <div className={styles.panel_title}>{FJLocalStore._(title)}</div>
                    <FJModelFilterTabs
                        show={isShow}
                        filters={filterConfig}
                        activeFilter={activeFilter}
                        onChange={setActiveFilter}
                    />
                </div>

                <FJModelList>
                    {filteredList.map(model => (
                        <FJModelItem
                            key={model.value}
                            data={model}
                            isSelected={model.value === currentModelValue}
                            onClick={handleSelect}
                            currentMode={currentMode}
                        />
                    ))}
                </FJModelList>
            </div>
        );
    };

    return (
        <FJDropdownNew
            renderTrigger={renderTrigger}
            renderOverlay={renderOverlay}
            offsetLeft={offsetLeft}
            offsetTop={offsetTop}
            trigger='click'
            placement='rightTop'
            useAnchorBody
            zIndex={FJUtil.isLandingPage() ? 4 : 999}
            canTriggerActive
            isNeedBindWheelEvent={false}
            onHide={() => setIsShow(false)}
            onShow={() => setIsShow(true)}
        />
    );
};

FJModelSelectorPanel.propTypes = {
    modelList: PropTypes.array.isRequired,
    filterConfig: PropTypes.array.isRequired,
    currentModelValue: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    currentMode: PropTypes.string,
    title: PropTypes.string,
    boxStyle: PropTypes.object,
    className: PropTypes.string,
    offsetLeft: PropTypes.number,
    offsetTop: PropTypes.number,
    maxHeight: PropTypes.string,
    sideModelClassName: PropTypes.string,
};

export default FJModelSelectorPanel;
